'use client';
import React, { useState } from 'react';
import { trackFormSubmission, trackFormStart, trackGAdsContactFormConversion } from '@/lib/analytics';

export default function ContactForm() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', businessType: '', preferredDate: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formStartedRef = React.useRef(false);

  const handleFormStart = () => {
    if (!formStartedRef.current) {
      formStartedRef.current = true;
      trackFormStart('Contact Form', '/contact');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || 'Failed to send your message. Please try again.');
      }
      trackFormSubmission('Contact Form', '/contact');
      trackGAdsContactFormConversion();
      setSubmitted(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="md:basis-1/2">
      {submitted ?
      <div className="rounded-2xl p-8 text-center" style={{ backgroundColor: '#ffffff' }}>
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#30a46c' }}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" width="24" height="24" className="text-white"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
          </div>
          <h3 className="text-xl font-semibold mb-2" style={{ color: '#08294a', fontFamily: 'Source Sans 3, sans-serif' }}>Inquiry received!</h3>
          <p className="text-sm" style={{ color: '#08294a', fontFamily: 'Source Sans 3, sans-serif' }}>We&apos;ve received your message and will contact you within one business day to confirm your free inspection.</p>
          <button onClick={() => {setSubmitted(false);setFormData({ name: '', email: '', phone: '', businessType: '', preferredDate: '', message: '' });}} className="mt-4 inline-flex items-center justify-center rounded-2xl border px-5 py-2.5 text-sm font-semibold" style={{ backgroundColor: '#004aad', borderColor: '#004aad', color: '#ffffff' }}>Send another message</button>
        </div> :

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error &&
        <div className="rounded-2xl px-5 py-3 text-sm" style={{ backgroundColor: '#fef2f2', color: '#b91c1c', border: '1px solid #fca5a5', fontFamily: 'Source Sans 3, sans-serif' }}>
              {error}
            </div>
        }
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium" style={{ color: '#08294a', fontFamily: 'Source Sans 3, sans-serif' }}>Name *</label>
            <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} onFocus={handleFormStart} placeholder="Your name" className="w-full rounded-2xl border px-5 py-3 text-sm outline-none" style={{ borderColor: '#08294a', color: '#08294a', backgroundColor: '#ffffff', fontFamily: 'Source Sans 3, sans-serif' }} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium" style={{ color: '#08294a', fontFamily: 'Source Sans 3, sans-serif' }}>Email *</label>
            <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} onFocus={handleFormStart} placeholder="your@email.com" className="w-full rounded-2xl border px-5 py-3 text-sm outline-none" style={{ borderColor: '#08294a', color: '#08294a', backgroundColor: '#ffffff', fontFamily: 'Source Sans 3, sans-serif' }} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium" style={{ color: '#08294a', fontFamily: 'Source Sans 3, sans-serif' }}>Phone</label>
            <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} onFocus={handleFormStart} placeholder="555 555 5555" className="w-full rounded-2xl border px-5 py-3 text-sm outline-none" style={{ borderColor: '#08294a', color: '#08294a', backgroundColor: '#ffffff', fontFamily: 'Source Sans 3, sans-serif' }} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium" style={{ color: '#08294a', fontFamily: 'Source Sans 3, sans-serif' }}>What type of facility do you operate? *</label>
            <select required value={formData.businessType} onChange={(e) => setFormData({ ...formData, businessType: e.target.value })} onFocus={handleFormStart} className="w-full rounded-2xl border px-5 py-3 text-sm outline-none" style={{ borderColor: '#08294a', color: '#08294a', fontFamily: 'Source Sans 3, sans-serif', backgroundColor: '#ffffff' }}>
              <option value="">Select...</option>
              <option value="restaurant">Restaurant</option>
              <option value="hotel">Hotel or catering kitchen</option>
              <option value="commercial">Commercial kitchen</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium" style={{ color: '#08294a', fontFamily: 'Source Sans 3, sans-serif' }}>When would you prefer your inspection?</label>
            <div className="flex flex-col gap-2">
              {['This week', 'Next week', 'Flexible'].map((opt) =>
            <label key={opt} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="preferredDate" value={opt} checked={formData.preferredDate === opt} onChange={(e) => {handleFormStart();setFormData({ ...formData, preferredDate: e.target.value });}} style={{ accentColor: '#004aad' }} />
                  <span className="text-sm" style={{ color: '#08294a', fontFamily: 'Source Sans 3, sans-serif' }}>{opt}</span>
                </label>
            )}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium" style={{ color: '#08294a', fontFamily: 'Source Sans 3, sans-serif' }}>Message</label>
            <textarea rows={4} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} onFocus={handleFormStart} placeholder="Tell us about your kitchen..." className="w-full rounded-2xl border px-5 py-3 text-sm outline-none resize-none" style={{ borderColor: '#08294a', color: '#08294a', backgroundColor: '#ffffff', fontFamily: 'Source Sans 3, sans-serif' }} />
          </div>
          <button type="submit" disabled={loading} className="inline-flex items-center justify-center rounded-2xl border px-5 py-2.5 text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed" style={{ backgroundColor: '#004aad', borderColor: '#004aad', color: '#ffffff' }}>
            {loading ? 'Sending...' : 'Schedule inspection'}
          </button>
        </form>
      }
    </div>
  );
}
